// pages/ProfilePage.jsx
// Profile Page - MERGED: Original features + Settings link + Photo upload + Working toggles
// UPDATED Dec 30, 2025: Task 3.1 - Added LoyaltyTierBadge display
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { 
  Camera, 
  Edit, 
  LogOut, 
  TrendingUp, 
  Shield, 
  Award, 
  Briefcase, 
  ChevronRight, 
  Package, 
  X, 
  Check, 
  MapPin, 
  Phone, 
  User, 
  AlertCircle,
  Settings,
  Loader2,
  Coins,
  Crown,
  Gift
} from 'lucide-react';
import BottomNav from '../components/common/BottomNav';
import ReliabilityScore from '../components/trust/ReliabilityScore';
import SkillBadgeList from '../components/trust/SkillBadgeList';
import LoyaltyTierBadge from '../components/loyalty/LoyaltyTierBadge';
import { useAuth } from '../context/AuthContext';
import { useTrust } from '../context/TrustContext';
import { useSkills } from '../context/SkillContext';
import { useNavigate } from 'react-router-dom';
import { useProfileBonus } from '../hooks/useProfileBonus';
import { useNotification } from '../context/NotificationContext';
import { useTokens } from '../context/TokenContext';
import { useTheme } from '../context/ThemeContext';
import { uploadProfilePicture, getInitialsAvatar } from '../services/cloudinaryService';
import { formatTokens } from '../utils/tokenEconomy';
import Tooltip, { TrustScoreTooltip, SavingsTooltip, LoyaltyTierTooltip } from '../components/common/Tooltip';

const ProfilePage = () => {
  const { user, logout, getActiveUser } = useAuth();
  const { userTrust, getTrustBadge } = useTrust();
  const { userSkills } = useSkills();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { balance,refreshBalance } = useTokens();
  const { isDarkMode, preferences, toggleDarkMode, updatePreference } = useTheme();
  const fileInputRef = useRef(null);
  
  // Get active user (handles both member and vendor)
  const activeUser = getActiveUser?.() || user;
  
  // Profile bonus hook
  const { checkAndAwardBonus, bonusAwarded, checked: bonusChecked } = useProfileBonus(activeUser);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(
    activeUser?.profileImage || activeUser?.photoURL || null
  );
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    zipCode: '',
    city: '',
    bio: ''
  });
  
  // Initialize form data from user
  useEffect(() => {
    if (activeUser) {
      setFormData({
        name: activeUser.name || activeUser.profile?.name || '',
        phone: activeUser.phone || activeUser.profile?.phone || '',
        zipCode: activeUser.location?.zipCode || activeUser.profile?.location?.zipCode || '',
        city: activeUser.location?.city || activeUser.profile?.location?.city || '',
        bio: activeUser.bio || activeUser.profile?.bio || ''
      });
      setProfilePhoto(activeUser.profileImage || activeUser.photoURL || null);
    }
  }, [activeUser]);
  
  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };
  
  const trustBadge = getTrustBadge(userTrust?.score || 3.8);
  
  // Combine verified and pending skills for display
  const allUserSkills = [
    ...(userSkills?.verified || []),
    ...(userSkills?.pending || [])
  ];
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };
  
  // Validate phone number
  const validatePhone = (phone) => {
    if (!phone) return true; // Optional
    const phoneRegex = /^[\d\s\-\(\)\+]{10,}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };
  
  // Validate ZIP code
  const validateZipCode = (zip) => {
    if (!zip) return true; // Optional
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };

  // Handle profile photo upload
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);

    try {
      const result = await uploadProfilePicture(file, activeUser?.id);
      
      if (result.success) {
        setProfilePhoto(result.medium || result.url);
        
        // Update user profile in backend
        const token = localStorage.getItem('token');
        if (token) {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
          await fetch(`${apiUrl}/auth/update-profile`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              profileImage: result.url,
            }),
          });
        }

        // Update localStorage
        const savedUser = JSON.parse(localStorage.getItem('uniledge_member_auth') || '{}');
        savedUser.photoURL = result.medium || result.url;
        savedUser.profileImage = result.medium || result.url;
        localStorage.setItem('uniledge_member_auth', JSON.stringify(savedUser));

        addNotification?.({
          type: 'success',
          title: 'Profile Photo Updated',
          message: 'Your profile photo has been updated!',
          showToast: true,
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      addNotification?.({
        type: 'error',
        title: 'Upload Failed',
        message: error.message || 'Failed to upload photo. Please try again.',
        showToast: true,
      });
    } finally {
      setUploadingPhoto(false);
    }
  };
  
  // Save profile
  const handleSave = async () => {
    // Validate
    if (!validatePhone(formData.phone)) {
      setError('Please enter a valid phone number');
      return;
    }
    
    if (!validateZipCode(formData.zipCode)) {
      setError('Please enter a valid 5-digit ZIP code');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in again');
        setSaving(false);
        return;
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
      
      // Save to backend
      const response = await fetch(`${apiUrl}/auth/update-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          zipCode: formData.zipCode,
          city: formData.city,
          bio: formData.bio
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save profile');
      }
      
      // Success - exit edit mode
      setIsEditing(false);
      
      // Show success notification
      addNotification?.({
        type: 'success',
        title: 'Profile Updated',
        message: 'Your profile has been saved successfully.',
        showToast: true
      });
      
      // Check for profile completion bonus
      const profileData = {
        name: formData.name,
        email: activeUser?.email,
        phone: formData.phone,
        profile: { phone: formData.phone },
        location: { zipCode: formData.zipCode, city: formData.city }
      };
      
      const bonusResult = await checkAndAwardBonus(profileData);
      
      if (bonusResult.success) {
        // Refresh token balance
        refreshBalance?.();
        
        // Show bonus notification
        setTimeout(() => {
          addNotification?.({
            type: 'success',
            title: 'Profile Bonus Earned! 🎉',
            message: `You earned ${bonusResult.tokensAwarded} tokens for completing your profile!`,
            showToast: true
          });
        }, 500);
      }
      
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };
  
  // Cancel editing
  const handleCancel = () => {
    // Reset form to original values
    setFormData({
      name: activeUser?.name || activeUser?.profile?.name || '',
      phone: activeUser?.phone || activeUser?.profile?.phone || '',
      zipCode: activeUser?.location?.zipCode || activeUser?.profile?.location?.zipCode || '',
      city: activeUser?.location?.city || activeUser?.profile?.location?.city || '',
      bio: activeUser?.bio || activeUser?.profile?.bio || ''
    });
    setIsEditing(false);
    setError('');
  };
  
  // Check if profile is complete enough for bonus display
  const isProfileComplete = () => {
    const hasName = !!formData.name;
    const hasPhone = !!formData.phone;
    const hasLocation = !!(formData.zipCode || formData.city);
    const filledCount = [hasName, hasPhone, hasLocation].filter(Boolean).length;
    return filledCount >= 2;
  };

  // Toggle component for settings
  const Toggle = ({ enabled, onChange }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`w-10 h-6 rounded-full p-1 flex transition-colors ${
        enabled ? 'bg-blue-600 justify-end' : 'bg-gray-300 justify-start'
      }`}
    >
      <div className="w-4 h-4 bg-white rounded-full shadow"></div>
    </button>
  );
  
  return (
    <div className="container mx-auto px-4 pb-20">
      {/* Header with Settings Icon */}
      <div className="mt-6 mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Profile</h1>
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/wallet')} 
            className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-full"
          >
            <Coins className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700">
              {formatTokens(balance || 0)}
            </span>
          </button>
          <button 
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-2"
            title="Settings"
          >
            <Settings size={22} className="text-gray-600" />
          </button>
        </div>
      </div>
      
      {/* Profile Completion Bonus Banner */}
      {bonusChecked && !bonusAwarded && !isProfileComplete() && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <Award className="w-5 h-5 text-yellow-900" />
            </div>
            <div>
              <p className="font-semibold text-yellow-900">
                Complete your profile for 20 bonus tokens! 🎁
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Add your phone number and location to unlock your reward.
              </p>
              <button 
                onClick={() => setIsEditing(true)}
                className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
              >
                Complete Now →
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Header with Trust Score */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center">
          <div className="relative">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
              {uploadingPhoto ? (
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              ) : profilePhoto ? (
                <img 
                  src={profilePhoto} 
                  alt={activeUser?.name} 
                  className="w-full h-full rounded-full object-cover" 
                />
              ) : (
                <img
                  src={getInitialsAvatar(activeUser?.name || activeUser?.email || 'User')}
                  alt={activeUser?.name}
                  className="w-full h-full rounded-full object-cover"
                />
              )}
            </div>
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-0 right-0 bg-blue-600 p-1.5 rounded-full hover:bg-blue-700 disabled:opacity-50"
            >
              <Camera size={14} className="text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>
          
          <div className="ml-4 flex-1">
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                className="text-lg font-semibold w-full border-b-2 border-blue-500 focus:outline-none pb-1"
              />
            ) : (
              <h2 className="text-lg font-semibold">
                {activeUser?.name || activeUser?.email?.split('@')[0] || 'User Name'}
              </h2>
            )}
            <p className="text-gray-600">{activeUser?.email || 'user@example.com'}</p>
            
            {/* Loyalty Tier Badge - NEW */}
            <div className="mt-2">
              <LoyaltyTierBadge 
                size="small" 
                showProgress={false}
                expandable={false}
              />
            </div>
            
            {!isEditing ? (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                <span>Edit Profile</span>
                <Edit size={14} className="ml-1" />
              </button>
            ) : (
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : (
                    <Check size={14} className="mr-1" />
                  )}
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center px-3 py-1 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                >
                  <X size={14} className="mr-1" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
        
        {/* Trust Score Section */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-700">Community Trust Score</h3>
              <TrustScoreTooltip score={userTrust?.score || 3.8} />
            </div>
            <span className="text-xs text-gray-500">Updated today</span>
          </div>
          <ReliabilityScore 
            score={userTrust?.score || 3.8}
            breakdown={userTrust?.breakdown}
            showDetails={true}
            size="medium"
          />
          <p className="text-sm text-gray-600 mt-2">{trustBadge.description}</p>
        </div>
      </div>
      
      {/* Loyalty Status Card - WITH TOOLTIP */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">Loyalty Status</h3>
            <LoyaltyTierTooltip tier={activeUser?.loyaltyTier || 'NEW_USER'} />
          </div>
          <Crown className="w-5 h-5 text-yellow-500" />
        </div>
        <div className="p-4">
          <LoyaltyTierBadge 
            size="medium"
            showProgress={true}
            expandable={true}
            onViewDetails={() => navigate('/wallet')}
          />
          
          <div className="flex items-center gap-2 text-sm text-gray-600 mt-4 pt-3 border-t">
            <Gift className="w-4 h-4" />
            <span>Higher tiers unlock bigger token redemptions at checkout</span>
          </div>
        </div>
      </div>
      
      {/* My Orders Card */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-medium">My Orders</h3>
          <Package className="w-5 h-5 text-gray-400" />
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">View Order History</p>
              <p className="text-sm text-gray-600">Track orders, view receipts, and reorder</p>
            </div>
            <button 
              onClick={() => navigate('/orders')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center"
            >
              View Orders
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Skills & Verification Card - ENHANCED */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-medium">Skills & Certifications</h3>
          <Award className="w-5 h-5 text-gray-400" />
        </div>
        <div className="p-4">
          {/* Skills Summary */}
          {allUserSkills.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{allUserSkills.length} Skills</p>
                  <p className="text-sm text-gray-600">
                    {userSkills?.verified?.length || 0} verified, {userSkills?.pending?.length || 0} pending
                  </p>
                </div>
                <button onClick={() => navigate('/skills')} className="hover:bg-gray-100 rounded p-1 transition-colors">
                  <ChevronRight className="w-5 h-5 text-gray-400 hover:text-blue-600" />
                </button>
              </div>
              
              {/* Display skill badges */}
              <SkillBadgeList
                skills={allUserSkills}
                maxDisplay={3}
                size="small"
                showAddButton={true}
                onAddClick={() => navigate('/skills')}
                onSkillClick={() => navigate('/skills')}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Verify Your Skills</p>
                <p className="text-sm text-gray-600">Unlock premium job opportunities</p>
              </div>
              <button 
                onClick={() => navigate('/skills')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Get Started
              </button>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm text-gray-600 mt-3 pt-3 border-t">
            <Shield className="w-4 h-4" />
            <span>Verified skills increase your trust score</span>
          </div>
        </div>
      </div>

      {/* Earning Opportunities Card */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-medium">Earning Opportunities</h3>
          <Briefcase className="w-5 h-5 text-gray-400" />
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Weekend Jobs Available</p>
              <p className="text-sm text-gray-600">Use your skills to earn tokens</p>
            </div>
            <button 
              onClick={() => navigate('/earn')}
              className="text-blue-600 font-medium text-sm"
            >
              View Jobs →
            </button>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">New:</span> Professional butchers needed this weekend! 
              Earn up to 400 tokens.
            </p>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Account Information - Editable */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-medium">Account Information</h3>
            {!isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                className="text-blue-600 text-sm hover:text-blue-700"
              >
                Edit
              </button>
            )}
          </div>
          
          {/* Phone Number */}
          <div className="p-4 flex justify-between items-center border-b">
            <div className="flex items-center">
              <Phone className="w-4 h-4 text-gray-400 mr-2" />
              <span>Phone Number</span>
            </div>
            {isEditing ? (
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="text-right border-b border-blue-300 focus:outline-none focus:border-blue-500 w-40"
              />
            ) : (
              <span className="text-gray-600">{formData.phone || 'Not set'}</span>
            )}
          </div>
          
          {/* ZIP Code */}
          <div className="p-4 flex justify-between items-center border-b">
            <div className="flex items-center">
              <MapPin className="w-4 h-4 text-gray-400 mr-2" />
              <span>ZIP Code</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                placeholder="95823"
                maxLength={10}
                className="text-right border-b border-blue-300 focus:outline-none focus:border-blue-500 w-24"
              />
            ) : (
              <span className="text-gray-600">{formData.zipCode || 'Not set'}</span>
            )}
          </div>
          
          {/* City */}
          <div className="p-4 flex justify-between items-center border-b">
            <div className="flex items-center">
              <MapPin className="w-4 h-4 text-gray-400 mr-2" />
              <span>City</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Sacramento"
                className="text-right border-b border-blue-300 focus:outline-none focus:border-blue-500 w-32"
              />
            ) : (
              <span className="text-gray-600">{formData.city || 'Not set'}</span>
            )}
          </div>
          
          {/* Joined Date (read-only) */}
          <div className="p-4 flex justify-between items-center">
            <span>Joined Date</span>
            <span className="text-gray-600">
              {activeUser?.joinedDate || activeUser?.profile?.createdAt 
                ? new Date(activeUser.joinedDate || activeUser.profile.createdAt).toLocaleDateString() 
                : 'Not available'}
            </span>
          </div>
        </div>

        {/* Analytics Card */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h3 className="font-medium">Your Analytics</h3>
          </div>
          <div className="p-4 flex justify-between items-center">
            <div>
              <span className="font-medium">View your savings and impact</span>
              <p className="text-sm text-gray-600">Track your savings, group activity, and community impact</p>
            </div>
            <button 
              onClick={() => navigate('/analytics')}
              className="text-blue-600"
            >
              <TrendingUp size={20} />
            </button>
          </div>
        </div>

        {/* Settings Card - NOW WITH WORKING TOGGLES */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-medium">Settings</h3>
            <button 
              onClick={() => navigate('/settings')}
              className="text-blue-600 text-sm hover:text-blue-700"
            >
              All Settings
            </button>
          </div>
          <div className="p-4 flex justify-between items-center border-b">
            <span>Notifications</span>
            <Toggle 
              enabled={preferences?.pushNotifications ?? true} 
              onChange={(val) => updatePreference('pushNotifications', val)} 
            />
          </div>
          <div className="p-4 flex justify-between items-center">
            <span>Dark Mode</span>
            <Toggle 
              enabled={isDarkMode} 
              onChange={toggleDarkMode} 
            />
          </div>
        </div>
        
        <button 
          onClick={handleLogout}
          className="w-full bg-red-100 text-red-600 py-3 rounded-lg flex items-center justify-center mt-6 hover:bg-red-200 transition-colors"
        >
          <LogOut size={18} className="mr-2" />
          <span>Log Out</span>
        </button>
      </div>
      
      <BottomNav />
    </div>
  );
};

export default ProfilePage;